import * as S from '@radix-ui/react-select'
import { Check, ChevronDown } from 'lucide-react'
import { EntityIcon } from '../EntityIcon'
import { normalizeTone } from './optionSet'
import s from './Select.module.css'

/**
 * ONE option payload, carrying its own availability (#1075).
 *
 * `selectable: false` means the value may not be NEWLY chosen — a vocabulary
 * entry the deployment has archived. It does NOT mean the option disappears,
 * and the difference is the whole point: the LABEL of a value already stored on
 * a record is resolved from this same array. Drop the row to express
 * unavailability and a historical value renders as the placeholder — the value
 * looks *absent* rather than *archived*, which is worse than showing it.
 *
 * So: one semantic source, never parallel option/label arrays. A renderer that
 * wants to disable an item reads `selectable`; a renderer that wants a label
 * reads `label`; the two cannot disagree because they are the same row.
 *
 * Absent reads as SELECTABLE, deliberately. Every catalogue that declares no
 * availability rule — and an older core that does not answer the question at
 * all — must keep behaving exactly as it did.
 */
export interface SelectOption {
  value: string
  label: string
  /** false ⇒ present and rendered, but closed to a NEW choice. Default: true. */
  selectable?: boolean
  /**
   * Optional GOVERNED decoration, carried on the same row as the label so the
   * two cannot disagree — the same argument `selectable` is here for.
   *
   * `tone` is one of `Pill`'s five and `icon` is an `EntityIcon` registry key.
   * Neither is CSS and neither may be a colour: an unrecognised value falls
   * back to neutral rather than painting nothing, which is what an undeclared
   * token does and does silently.
   */
  tone?: string | null
  icon?: string | null
}

/** The marker an unavailable option carries, on the item AND on the trigger. */
export const UNAVAILABLE_MARKER = 'Archived'

export const isSelectable = (o: SelectOption): boolean => o.selectable !== false

/**
 * The sentinel a "clear this field" item carries.
 *
 * Radix refuses an item whose `value` is the empty string — it reserves that
 * for "no selection" — so an EMPTY choice cannot be an ordinary option. The
 * sentinel never leaves this file: `onValueChange` translates it back to `''`,
 * which is what an unfilled field actually holds.
 */
const CLEAR_VALUE = '__solum_clear__'

export function Select({
  value, onValueChange, options, placeholder, disabled, testId,
  autoFocus, open, defaultOpen, onOpenChange, clearLabel, ...aria
}: {
  value?: string
  onValueChange: (v: string) => void
  options: SelectOption[]
  placeholder?: string
  disabled?: boolean
  /** forwarded to the trigger for tests/automation (the trigger is the interactive node) */
  testId?: string
  /**
   * Focus the trigger on mount.
   *
   * A host that swaps a read view for this control OWES the keyboard a landing
   * place. The record page did not: entering edit removed the `tabIndex` from
   * the element that had focus, so focus fell to `<body>` and the editor had to
   * be found again from the top of the document.
   */
  autoFocus?: boolean
  /** Told when the menu opens and closes — a host that entered an edit mode to
   *  show this control needs to know when to leave it. */
  onOpenChange?: (open: boolean) => void
  /** Controlled / initial menu state, passed to Radix. Absent, the menu is
   *  uncontrolled as before; present, a host, a gallery or a design capture can
   *  show the open menu without driving a pointer (#2406 kit-defects-b). */
  open?: boolean
  defaultOpen?: boolean
  /**
   * Offer an explicit EMPTY choice, worded by the caller ("None", "No policy").
   *
   * Absent by default: a required field must not offer one. Present, it is the
   * only way to unset a nullable option from a picker — the record page's old
   * free-text editor could be emptied, and replacing it with a Select silently
   * took that away.
   */
  clearLabel?: string
  /* The trigger is the focusable node, so `id` and the aria attributes belong on
     IT — not on the Root, which renders nothing. <Field> clones these onto its
     child, and before this they were silently dropped: a Select inside a Field
     had a label that pointed at nothing (UX-01). */
  id?: string
  'aria-describedby'?: string
  'aria-invalid'?: boolean
  'aria-required'?: boolean
  'aria-labelledby'?: string
  /* A caller that knows what this control is FOR says so. Two Selects on one
     page — an update policy and a funding contract — are otherwise announced
     identically, and a screen-reader user cannot tell which is which. */
  'aria-label'?: string
}) {
  /**
   * ── THERE IS NO IN-MENU FILTER, AND THAT IS A DECISION ────────────────────
   * A first cut of this control put a filter input above the list for option
   * sets longer than eight, to answer #1466 row 6. It shipped in review and
   * came back with two measurements that killed it, both against the installed
   * `@radix-ui/react-select@2.3.2`:
   *
   *   · `role="listbox"` sits on the CONTENT element (`dist/index.mjs:480`),
   *     so an input rendered anywhere inside it is a `textbox` CHILD OF A
   *     LISTBOX. That is not a permitted structure; assistive technology is
   *     entitled to ignore the input entirely and to mis-report the options.
   *   · `useEffect(() => { if (isPositioned) focusSelectedItem() })` runs AFTER
   *     mount, so it overrides `autoFocus` on the input. The filter never held
   *     focus on open: a keyboard user landed on an option and their next
   *     keystroke went to Radix's own type-ahead. The feature was mouse-only,
   *     and the comment claiming the list "stays keyboard reachable" was false.
   *
   * It cannot be made correct inside a listbox, and making a long set open a
   * DIFFERENT control is precisely what row 6 forbids. So the filter is gone
   * rather than patched. Radix's type-ahead already jumps to an option by
   * first letters, and the platform's governed searchable picker is `Lookup`.
   *
   * Row 6's "filters in place" is therefore UNMET, not quietly dropped: it
   * needs a combobox with `aria-controls`/`aria-activedescendant`, which is a
   * different control to build and not a prop to add here.
   */
  return (
    // Preserve an explicitly controlled empty value. Converting '' to undefined
    // gives Radix its own state, allowing an unconfirmed proposal to look current.
    <S.Root value={value} disabled={disabled} open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange}
      onValueChange={(next) => onValueChange(next === CLEAR_VALUE ? '' : next)}>
      {/* The fallback is a LAST resort, not an override. A real <label>, an
          aria-labelledby, or a name the caller passed must win — until
          2026-09-04 this line spread the caller's aria props and then overwrote
          `aria-label` with `placeholder ?? 'Select'`, so every Select without a
          placeholder was announced as the word "Select", including the two on
          the portal's Environments page. Measured through the customer portal
          on a live Q8 environment (SLM-MOONBASE #139): the page asks for
          "Update policy for <environment>" and the control answered "Select". */}
      <S.Trigger
        className={s.trigger}
        data-testid={testId}
        autoFocus={autoFocus}
        {...aria}
        aria-label={aria.id || aria['aria-labelledby'] || aria['aria-label']
          ? aria['aria-label']
          : (placeholder ?? 'Select')}
      >
        <S.Value placeholder={placeholder ?? 'Select…'} />
        <S.Icon><ChevronDown size={15} /></S.Icon>
      </S.Trigger>
      <S.Portal>
        <S.Content className={s.content} position="popper" sideOffset={4}>
          <S.Viewport className={s.viewport}>
            {clearLabel && (
              <S.Item className={s.item} value={CLEAR_VALUE}
                data-testid={testId ? `${testId}-clear` : undefined}>
                <S.ItemText><span className={s.itemClear}>{clearLabel}</span></S.ItemText>
                <S.ItemIndicator className={s.check}><Check size={14} /></S.ItemIndicator>
              </S.Item>
            )}
            {options.map((o) => {
              const open = isSelectable(o)
              return (
                /* The marker lives INSIDE ItemText on purpose. Radix renders the
                   selected item's ItemText into the trigger, so an archived
                   current value reads "Old partner Archived" on the closed
                   control as well as in the open menu — one element, both
                   surfaces, nothing to keep in sync. */
                <S.Item
                  key={o.value}
                  value={o.value}
                  disabled={!open}
                  className={s.item}
                  data-unavailable={open ? undefined : ''}
                >
                  <S.ItemText>
                    {/* Governed decoration, on the item AND therefore on the
                        trigger — Radix renders the selected item's ItemText
                        into it, so the closed control carries the same icon and
                        tone as the menu with nothing to keep in sync. */}
                    {o.icon && <EntityIcon iconKey={o.icon} size={14} className={s.itemIcon} />}
                    <span className={s.itemLabel} data-tone={normalizeTone(o.tone)}>{o.label}</span>
                    {!open && <span className={s.marker}>{UNAVAILABLE_MARKER}</span>}
                  </S.ItemText>
                  <S.ItemIndicator className={s.indicator}><Check size={14} /></S.ItemIndicator>
                </S.Item>
              )
            })}
          </S.Viewport>
        </S.Content>
      </S.Portal>
    </S.Root>
  )
}
